import { ImageVideoPreview } from "../../../common/ImageVideoPreview";
import { AnimationPreviewWrapper } from "./AnimationPreviewWrapper";
import { DraggableComponent } from "./DraggableComponent";
import { ITEM_TYPES, MediaItem, TabKeys } from "../../../../types";
import { getMaskClipStyle, isMasked } from "../../../../helpers/maskHelper";
import { enrichAssetWithPath } from "../../../../../animation/helpers/helpers";
import {
  resolveColorToBrandkitColorIfNeeded,
  roundToHalfPixel,
} from "../../../../helpers/itemsHelper";
import { useTemplatesStatusStore } from "../../../../store/useTemplatesStatusStore";
import { useTemplateEditorStore } from "../../../../store/useTemplateEditorStore";
import { generateStrokeStyles } from "../../../../helpers";
import { useCallback } from "react";
import { useTemplateData } from "../../../../hooks/useTemplateData";

export const TemplateImages = ({
  templateImages,
  previewWidth,
  previewHeight,
  editable = false,
}: {
  templateImages: MediaItem[];
  previewWidth: number;
  previewHeight: number;
  editable?: boolean;
}) => {
  const { updateImage, currentItem } = useTemplateEditorStore((state) => ({
    updateImage: state.updateImage,
    currentItem: state.templateState.currentItem,
  }));

  const { setActiveTab, activeTab } = useTemplatesStatusStore((state) => ({
    setActiveTab: state.setActiveTab,
    activeTab: state.activeTab,
  }));

  const {
    brandKitData: { brandKitPalette },
    placeholderAssets,
  } = useTemplateData();

  const getImageComponent = useCallback(
    (image: MediaItem) => {
      const styles = generateStrokeStyles(
        {
          ...image,
          stroke: {
            ...image.stroke,
            color: resolveColorToBrandkitColorIfNeeded(
              image.stroke?.color ?? undefined,
              brandKitPalette
            ),
          },
        },
        previewWidth,
        previewHeight
      );
      // Clip the media to the mask here (on the media element) so the crop
      // travels with enter/leave animations. While editing this item the clip
      // is dropped (full media shown, MaskEditor handles the dim).
      const isEditingThis = editable && currentItem?.id === image.id;
      const cropStyle =
        isMasked(image.mask) && !isEditingThis
          ? getMaskClipStyle(image.mask)
          : undefined;
      return (
        <ImageVideoPreview
          type={ITEM_TYPES.Image}
          assetId={image.assetId}
          path={image.path}
          style={{ ...styles, ...cropStyle }}
          placeholderAssets={placeholderAssets}
        />
      );
    },
    [
      previewWidth,
      previewHeight,
      brandKitPalette,
      placeholderAssets,
      editable,
      currentItem,
    ]
  );

  if (templateImages.length === 0) return null;

  if (!editable) {
    return (
      <>
        {templateImages.map((image) => {
          if (!image.path) {
            image = enrichAssetWithPath(image, templateImages);
          }

          const strokeWidth = Math.round(
            (image.stroke?.width ?? 0) * previewWidth
          );
          const doubleStrokeWidth = strokeWidth * 2;

          return (
            <div
              key={image.id}
              style={{
                position: "absolute",
                top:
                  roundToHalfPixel(image.position.y * previewHeight) -
                  strokeWidth,
                left:
                  roundToHalfPixel(image.position.x * previewWidth) -
                  strokeWidth,
                width:
                  roundToHalfPixel(image.size.width * previewWidth) +
                  doubleStrokeWidth,
                height:
                  roundToHalfPixel(image.size.height * previewHeight) +
                  doubleStrokeWidth,
                zIndex: image.layerIndex,
                transformOrigin: "center",
                transform: image.rotation
                  ? `rotate(${image.rotation}deg)`
                  : undefined,
              }}
            >
              {getImageComponent(image)}
            </div>
          );
        })}
      </>
    );
  }

  return (
    <>
      {templateImages.map((image) => {
        return (
          <DraggableComponent
            key={image.id}
            position={image.position}
            size={image.size}
            rotation={image.rotation}
            layerIndex={image.layerIndex}
            onDragStop={updateImage}
            onRotate={(rotation) => updateImage({ id: image.id, rotation })}
            saveRatio={true}
            drag={true}
            resize={true}
            mask={image.mask}
            onMask={(mask) => updateImage({ id: image.id, mask })}
            item={{
              type: ITEM_TYPES.Image,
              id: image.id,
            }}
            onFocus={() => {
              if (
                activeTab !== TabKeys.objects &&
                activeTab !== TabKeys.motion
              ) {
                setActiveTab(TabKeys.objects);
              }
            }}
            previewWidth={previewWidth}
            previewHeight={previewHeight}
            stroke={image.stroke}
          >
            <AnimationPreviewWrapper
              animations={image.animations}
              previewWidth={previewWidth}
              previewHeight={previewHeight}
              itemId={image.id}
              rotation={image.rotation}
            >
              {getImageComponent(image)}
            </AnimationPreviewWrapper>
          </DraggableComponent>
        );
      })}
    </>
  );
};
